/**
 * 客户端设备/环境信息采集工具。
 *
 * 采集原则：
 * 1. 仅采集无需用户授权即可获取的公开信息（屏幕、视口、语言、时区等）；
 * 2. 需要权限的敏感 API（Geolocation、Notification、Clipboard 等）一律跳过；
 * 3. 每个字段独立 try/catch，浏览器不支持时自动缺省，保证兼容性；
 * 4. 采集逻辑全部为同步属性读取，耗时 <1ms，不影响页面加载性能。
 *
 * 地理信息：前端不采集（Geolocation 需授权），由后端通过 IP 反查体现。
 * 输出经 URL-safe base64 编码后通过 client_info 参数传给后端。
 */

/**
 * 采集无需授权的设备/环境信息。
 * @returns {Object} 设备信息对象，字段缺失时不写入对应键。
 */
export function collectClientInfo() {
    const info = {};
    const nav = window.navigator || {};
    const scr = window.screen || {};

    // 屏幕 resolution（物理屏幕尺寸）
    try {
        if (scr.width !== undefined && scr.height !== undefined) {
            info.screen_resolution = scr.width + 'x' + scr.height;
        }
    } catch (e) { /* 部分隐私模式下 screen 可能不可用 */ }

    // 视口尺寸（浏览器可视区域）
    try {
        if (window.innerWidth !== undefined && window.innerHeight !== undefined) {
            info.viewport_size = window.innerWidth + 'x' + window.innerHeight;
        }
    } catch (e) { /* ignore */ }

    // 设备像素比（高分屏为 2/3，影响渲染清晰度）
    try {
        if (window.devicePixelRatio !== undefined) {
            info.device_pixel_ratio = String(window.devicePixelRatio);
        }
    } catch (e) { /* ignore */ }

    // 颜色深度（位深，通常 24/30）
    try {
        if (scr.colorDepth !== undefined) {
            info.color_depth = String(scr.colorDepth);
        }
    } catch (e) { /* ignore */ }

    // 操作系统平台
    // 优先使用新标准 userAgentData.platform（Chromium 系），回退 navigator.platform
    try {
        if (nav.userAgentData && nav.userAgentData.platform) {
            info.platform = nav.userAgentData.platform;
        } else if (nav.platform) {
            info.platform = nav.platform;
        }
    } catch (e) { /* ignore */ }

    // 浏览器首选语言
    try {
        if (nav.language) {
            info.language = nav.language;
        }
    } catch (e) { /* ignore */ }

    // 时区（IANA 标识，如 Asia/Shanghai）
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) info.timezone = tz;
    } catch (e) { /* 旧浏览器可能不支持 Intl 时区解析 */ }

    // CPU 逻辑核心数
    try {
        if (nav.hardwareConcurrency !== undefined) {
            info.hardware_concurrency = String(nav.hardwareConcurrency);
        }
    } catch (e) { /* 部分浏览器出于隐私不暴露此字段 */ }

    // 设备内存（GB，仅 Chromium 系暴露，其他浏览器缺省）
    try {
        if (nav.deviceMemory !== undefined) {
            info.device_memory = String(nav.deviceMemory);
        }
    } catch (e) { /* ignore */ }

    // Cookie 是否启用
    try {
        if (nav.cookieEnabled !== undefined) {
            info.cookie_enabled = String(!!nav.cookieEnabled);
        }
    } catch (e) { /* ignore */ }

    // Do Not Track 用户反追踪偏好（值可能为 "1"/"0"/"unspecified"/null）
    try {
        const dnt = nav.doNotTrack || window.doNotTrack || nav.msDoNotTrack;
        if (dnt !== undefined && dnt !== null && dnt !== '') {
            info.do_not_track = String(dnt);
        }
    } catch (e) { /* ignore */ }

    // 触摸支持（移动端活动页的关键设备特征）
    try {
        const hasTouch = 'ontouchstart' in window ||
            (nav.maxTouchPoints !== undefined && nav.maxTouchPoints > 0);
        info.touch_support = String(!!hasTouch);
        if (nav.maxTouchPoints !== undefined) {
            info.max_touch_points = String(nav.maxTouchPoints);
        }
    } catch (e) { /* ignore */ }

    // 网络类型（实验性 API，Chromium 系支持，Safari/Firefox 缺省）
    try {
        const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
        if (conn) {
            if (conn.effectiveType) info.network_type = conn.effectiveType;
            if (conn.downlink !== undefined) info.network_downlink = String(conn.downlink);
            if (conn.rtt !== undefined) info.network_rtt = String(conn.rtt);
            if (conn.saveData !== undefined) info.save_data = String(!!conn.saveData);
        }
    } catch (e) { /* Network Information API 在部分浏览器需安全上下文 */ }

    // 在线状态（navigator.onLine）
    try {
        if (nav.onLine !== undefined) {
            info.online = String(!!nav.onLine);
        }
    } catch (e) { /* ignore */ }

    // 文档来源 referer（来源页 URL，无需授权）
    try {
        if (document.referrer) {
            info.referer = document.referrer;
        }
    } catch (e) { /* ignore */ }

    // WebView 检测：判断是否在 App 内嵌 WebView 中打开
    try {
        const ua = nav.userAgent || '';
        if (ua) {
            info.is_webview = String(/WebView|wv|Android.*; wv|iPhone.*FBAN/i.test(ua));
        }
    } catch (e) { /* ignore */ }

    return info;
}

/**
 * 将 Unicode 字符串编码为标准 base64。
 * 使用 unescape(encodeURIComponent()) 处理 UTF-8，兼容所有主流浏览器。
 * @param {string} str 原始字符串
 * @returns {string} base64 字符串
 */
function unicodeToBase64(str) {
    return btoa(unescape(encodeURIComponent(str)));
}

/**
 * 采集设备信息并编码为 URL-safe base64 字符串。
 * 用于通过 URL query 参数安全传输（避免 +、/、= 在 URL 中的转义问题）。
 * @returns {string} URL-safe base64 编码的 JSON 字符串；采集失败返回空串。
 */
export function encodeClientInfo() {
    try {
        const json = JSON.stringify(collectClientInfo());
        if (!json) return '';
        const b64 = unicodeToBase64(json);
        // URL-safe：+ -> -，/ -> _，去掉末尾填充符 =
        return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) {
        // 采集或编码失败时返回空串，后端会以 None 处理，不影响主流程
        return '';
    }
}

/**
 * 获取当前页面完整 URL（含 query string），用于审计用户实际访问的页面地址。
 * 截断至 512 字符，与后端 page_url 字段长度对齐。
 * @returns {string}
 */
export function getCurrentPageUrl() {
    try {
        const href = window.location.href || '';
        return href.length > 512 ? href.slice(0, 512) : href;
    } catch (e) {
        return '';
    }
}

/**
 * 构造 verify_signature 请求所需的客户端采集参数。
 * 集中组装 page_url 与 client_info，便于 API 层一次性注入。
 * @returns {Object} { page_url, client_info }
 */
export function buildVerifySignatureParams() {
    return {
        page_url: getCurrentPageUrl(),
        client_info: encodeClientInfo()
    };
}
